import React, { useCallback, useEffect, useState } from "react";
import { getUserFromToken } from "../../../utils/functions.ts";
import { getTodayEasternYMD } from "../../../utils/dateUtils.ts";
import { useMask } from "../../../Contexts/mask.context.jsx";
import { useNavBar } from "../../../Contexts/navbar.items.context.jsx";
import { version } from "../../../Contexts/variables.jsx";
import FBXCalendar from "../../Universals/FBXCalendar/fbxcalendar.component.jsx";
import "./adminData.style.scss";

const USAGE_ENDPOINT = "https://www.fbxchange.com/ReactPHP/usage.php";
const SHOW_ONLY_CLICKED_KEY = "FBX_ADMIN_DATA_SHOW_ONLY_CLICKED";
const getTodayYMD = () => getTodayEasternYMD();
const USER_ROLE_OPTIONS = ["None", "Scout", "Coach"];
const ROLE_FILTER_OPTIONS = ["All", "Director", "Scout", "Coach", "None"];

const isAdminUser = (user) => {
  const securityLevel = String(user?.userContactLevel ?? user?.security ?? user?.ADMIN ?? user?.admin ?? "").trim();
  return securityLevel === "1"
    || user?.ADMIN === true
    || user?.admin === true
    || String(user?.assignTask || "").trim().toLowerCase() === "yes";
};

const isFbxTeam = (team) => cleanTeamCode(team) === "FBX";

const getSearchedFields = (metadata) => {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return [];
  if (Array.isArray(metadata.fieldsSearched)) {
    return metadata.fieldsSearched.map((field) => String(field || "").trim()).filter(Boolean);
  }
  if (metadata.criteria && typeof metadata.criteria === "object" && !Array.isArray(metadata.criteria)) {
    return Object.keys(metadata.criteria);
  }
  return [];
};

const getSearchCriteriaParts = (metadata) => {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return [];
  if (!metadata.criteria || typeof metadata.criteria !== "object" || Array.isArray(metadata.criteria)) return [];

  return Object.entries(metadata.criteria)
    .map(([key, value]) => {
      const cleanKey = String(key || "").trim();
      const cleanValue = Array.isArray(value)
        ? value.map((item) => String(item || "").trim()).filter(Boolean).join(", ")
        : String(value ?? "").trim();
      if (!cleanKey || !cleanValue) return "";
      return `${cleanKey}: ${cleanValue}`;
    })
    .filter(Boolean);
};

const getUsageSummaryParts = (event) => {
  const metadata = event?.metadata;
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return [];

  const parts = [];
  if (metadata.resultCount !== undefined) {
    parts.push(`Count ${metadata.resultCount}`);
  }

  const fields = getSearchedFields(metadata);
  if (fields.length > 0) {
    parts.push(`Fields ${fields.join(", ")}`);
  }

  const criteria = getSearchCriteriaParts(metadata);
  if (criteria.length > 0) {
    parts.push(`Search ${criteria.join("; ")}`);
  }

  return parts;
};

const cleanTeamCode = (value) => String(value || "").trim().toUpperCase();

const hasTaskAddition = (user) => String(user?.assignTask || "").trim().toLowerCase() === "yes";
const getDisplayRole = (user) => hasTaskAddition(user) ? "Director" : (USER_ROLE_OPTIONS.includes(user?.role) ? user.role : "None");
const userMatchesRoleFilter = (user, roleFilter) => roleFilter === "All" || getDisplayRole(user) === roleFilter;

const getUsageActivityCount = (record) => Number(record?.usageCount ?? 0);

const getUsageListCount = (record, includeActivity = true) => {
  const signIns = Number(record?.signInCount ?? 0);
  if (!includeActivity) return signIns;
  const activity = getUsageActivityCount(record);
  if (signIns > 0 || activity <= 0) return signIns;
  return `${activity} activity`;
};

const getDateRangeLabel = (startDate, endDate, fallback = "Today") => {
  const start = String(startDate || "").trim();
  const end = String(endDate || "").trim();
  if (start && end) return start === end ? start : `${start} to ${end}`;
  return start || end || fallback;
};

const formatLoginDate = (value) => {
  const cleanDate = String(value || "").trim();
  const match = cleanDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return cleanDate || "-";

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
};

const TRACKED_ACTIONS = [
  "S1 Search",
  "S2 Transaction Search",
  "S2 Event Search",
  "S2 Scout Search",
  "S2 Note Search",
  "Lite Sign In",
  "Lite Quick List",
  "Lite S1 Search",
  "Lite Last List",
  "Lite L1 List",
  "Lite L2 List",
  "Lite SQ Saved Queries",
  "Lite CFL Teams",
  "Lite CFL Team",
  "Lite NFL Teams",
  "Lite NFL Team",
  "Lite Quick Note",
  "Lite Hard Data",
  "Lite Transactions",
  "Lite Notes",
  "Lite Reports",
  "White Board Opened",
  "BTN Opened",
  "Add/Edit Opened",
  "Overview Opened",
  "Export Opened",
  "Hard Data Opened",
  "Transactions Opened",
  "Reports Opened",
  "Notes Opened",
  "Files Opened",
  "QN Opened",
  "One Liner QN Opened",
  "L1 Lists Opened",
  "L2 Shared Lists Opened",
  "SQ Saved Query Opened",
  "A Alerts Opened",
  "SN Shared Notes Opened",
  "Purple Man Opened",
  "Calendar Opened",
  "LG Legend Opened",
];

const BUTTON_ACTION_GROUPS_2026 = [
  [
    "S1 Opened",
    "S2 Opened",
    "S3 Last Search Opened",
    "L1 Lists Opened",
    "L2 Shared Lists Opened",
    "SQ Saved Query Opened",
    "A Alerts Opened",
    "SN Shared Notes Opened",
    "Data Admin Panel Opened",
    "BluBars Opened",
  ],
  [
    "Purple Man Opened",
    "Calendar Opened",
    "LG Legend Opened",
    "Bug Report Opened",
  ],
  [
    "BTN Opened",
    "White Board Opened",
    "Tasks Opened",
    "Add/Edit Opened",
    "Overview Opened",
    "Export Opened",
    "Hard Data Opened",
    "Transactions Opened",
    "Reports Opened",
    "Notes Opened",
    "Files Opened",
    "QN Opened",
  ],
];

const BUTTON_ACTION_GROUPS_LITE = [
  [
    "Lite Sign In",
    "Lite Quick List",
    "Lite S1 Search",
    "Lite Last List",
    "Lite L1 List",
    "Lite L2 List",
    "Lite SQ Saved Queries",
  ],
  [
    "Lite CFL Teams",
    "Lite CFL Team",
    "Lite NFL Teams",
    "Lite NFL Team",
  ],
  [
    "Lite Quick Note",
    "Lite Hard Data",
    "Lite Transactions",
    "Lite Notes",
    "Lite Reports",
  ],
];

const BUTTON_ACTION_TABS = {
  "2026": BUTTON_ACTION_GROUPS_2026,
  lite: BUTTON_ACTION_GROUPS_LITE,
};

const BUTTON_ACTION_TAB_LABELS = {
  "2026": "2026",
  lite: "Lite",
};

const BUTTON_DISPLAY_LABELS = {
  "S1 Opened": "S1",
  "S2 Opened": "S2",
  "S3 Last Search Opened": "S3",
  "L1 Lists Opened": "L1",
  "L2 Shared Lists Opened": "L2",
  "SQ Saved Query Opened": "SQ",
  "A Alerts Opened": "A",
  "SN Shared Notes Opened": "SN",
  "Data Admin Panel Opened": "D",
  "BluBars Opened": "BB",
  "Lite Sign In": "Lite Sign In",
  "Lite Quick List": "Lite Quick List",
  "Lite S1 Search": "Lite S1 Search",
  "Lite Last List": "Lite Last List",
  "Lite L1 List": "Lite L1 List",
  "Lite L2 List": "Lite L2 List",
  "Lite SQ Saved Queries": "Lite SQ Saved Queries",
  "Lite CFL Teams": "Lite CFL Teams",
  "Lite CFL Team": "Lite CFL Team",
  "Lite NFL Teams": "Lite NFL Teams",
  "Lite NFL Team": "Lite NFL Team",
  "Lite Quick Note": "Lite Quick Note",
  "Lite Hard Data": "Lite Hard Data",
  "Lite Transactions": "Lite Transactions",
  "Lite Notes": "Lite Notes",
  "Lite Reports": "Lite Reports",
  "Purple Man Opened": "Purple Man",
  "Calendar Opened": "Calendar",
  "LG Legend Opened": "LG",
  "Bug Report Opened": "Bug",
  "White Board Opened": "White Board",
  "BTN Opened": "BTN",
  "Tasks Opened": "Tasks",
  "QN Opened": "QN",
};

const getButtonLabel = (label) => BUTTON_DISPLAY_LABELS[label] || String(label || "").replace(/\s+Opened$/, "");
const isLiteButtonAction = (label) => String(label || "").startsWith("Lite");

const AdminData = () => {
  const { setShowMask } = useMask();
  const { updateActiveSideButton } = useNavBar();
  const todayYMD = getTodayYMD();
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState("");
  const [usersError, setUsersError] = useState("");
  const [dateLabel, setDateLabel] = useState("");
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedScope, setSelectedScope] = useState("user");
  const [teamDisplayMode, setTeamDisplayMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [actionCounts, setActionCounts] = useState({});
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsError, setActionsError] = useState("");
  const [showOnlyClicked, setShowOnlyClicked] = useState(() => {
    try {
      return localStorage.getItem(SHOW_ONLY_CLICKED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [selectedAction, setSelectedAction] = useState("");
  const [actionEvents, setActionEvents] = useState([]);
  const [actionEventsLoading, setActionEventsLoading] = useState(false);
  const [actionEventsError, setActionEventsError] = useState("");
  const [adminUserId, setAdminUserId] = useState("");
  const [startDate, setStartDate] = useState(() => getTodayYMD());
  const [endDate, setEndDate] = useState(() => getTodayYMD());
  const [openCalendar, setOpenCalendar] = useState(null);
  const [buttonsOpen, setButtonsOpen] = useState(false);
  const [buttonCounts, setButtonCounts] = useState({});
  const [buttonsLoading, setButtonsLoading] = useState(false);
  const [buttonsError, setButtonsError] = useState("");
  const [selectedButtonAction, setSelectedButtonAction] = useState("");
  const [buttonUsers, setButtonUsers] = useState([]);
  const [buttonUsersLoading, setButtonUsersLoading] = useState(false);
  const [buttonUsersError, setButtonUsersError] = useState("");
  const [activeButtonTab, setActiveButtonTab] = useState("2026");
  const [excludeFbxButtons, setExcludeFbxButtons] = useState(false);
  const [adminFullAccess, setAdminFullAccess] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyUser, setHistoryUser] = useState(null);
  const [historyLogins, setHistoryLogins] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleMessage, setRoleMessage] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const selectedTeamRecord = teams.find((team) => cleanTeamCode(team.teamName) === cleanTeamCode(selectedTeam)) || null;
  const filteredUsers = users.filter((user) => userMatchesRoleFilter(user, roleFilter));
  const selectedUserIndex = filteredUsers.findIndex((user) => String(user?.userId || "") === String(historyUser?.userId || selectedUser?.userId || ""));

  useEffect(() => {
    setShowMask(true);
    return () => setShowMask(false);
  }, [setShowMask]);

  useEffect(() => {
    try {
      localStorage.setItem(SHOW_ONLY_CLICKED_KEY, showOnlyClicked ? "true" : "false");
    } catch {
      // Ignore storage failures; the toggle still works for the current session.
    }
  }, [showOnlyClicked]);

  useEffect(() => {
    if (!selectedUser || userMatchesRoleFilter(selectedUser, roleFilter)) return;
    setSelectedUser(null);
    setSelectedScope("user");
    setActionCounts({});
    setSelectedAction("");
    setActionEvents([]);
    setActionsError("");
    setRoleMessage("");
  }, [roleFilter, selectedUser]);

  const closePanel = () => {
    setShowMask(false);
    updateActiveSideButton("");
  };

  const handleStartDateChange = (value) => {
    const nextStartDate = value || getTodayYMD();
    setStartDate(nextStartDate);
    if (endDate && nextStartDate > endDate) {
      setEndDate(nextStartDate);
    }
  };

  const handleEndDateChange = (value) => {
    const nextEndDate = value || getTodayYMD();
    setEndDate(nextEndDate);
    if (startDate && nextEndDate < startDate) {
      setStartDate(nextEndDate);
    }
  };

  const handleToggleCalendar = (fieldName) => {
    if (!fieldName) {
      setOpenCalendar(null);
      return;
    }

    setOpenCalendar((current) => (current === fieldName ? null : fieldName));
  };

  const loadUserActions = useCallback(async (user, userId = adminUserId) => {
    const selectedUserId = String(user?.userId || "").trim();
    if (!selectedUserId || !userId) return;

    setTeamDisplayMode(false);
    setSelectedUser(user);
    setSelectedScope("user");
    setActionsLoading(true);
    setActionsError("");
    setActionCounts({});
    setSelectedAction("");
    setActionEvents([]);
    setActionEventsError("");

    try {
      const response = await fetch(USAGE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
	        body: new URLSearchParams({
	          script: "getTodayUserUsageCounts",
	          userId: String(userId),
	          selectedUserId,
	          startDate,
	          endDate,
	        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setActionsError(data?.error || "Unable to load user data.");
        setActionCounts({});
      } else {
        setActionCounts(data.counts || {});
      }
    } catch {
      setActionsError("Unable to load user data.");
      setActionCounts({});
    } finally {
      setActionsLoading(false);
    }
  }, [adminUserId, endDate, startDate]);

  const loadLoginHistory = useCallback(async (user, userId = adminUserId) => {
    const selectedUserId = String(user?.userId || "").trim();
    const cleanUserId = String(userId || "").trim();
    if (!selectedUserId || !cleanUserId) return;

    setHistoryUser(user);
    setHistoryLoading(true);
    setHistoryError("");
    setHistoryLogins([]);

    try {
      const response = await fetch(USAGE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          script: "getUserLoginHistory",
          userId: cleanUserId,
          selectedUserId,
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setHistoryError(data?.error || "Unable to load login history.");
      } else {
        setHistoryLogins(Array.isArray(data.logins) ? data.logins : []);
      }
    } catch {
      setHistoryError("Unable to load login history.");
    } finally {
      setHistoryLoading(false);
    }
  }, [adminUserId]);

  const openHistoryPanel = useCallback(() => {
    const nextUser = (selectedUser && userMatchesRoleFilter(selectedUser, roleFilter))
      ? selectedUser
      : filteredUsers[0] || null;
    if (!nextUser) {
      setHistoryError("Select a person first.");
      setHistoryOpen(true);
      return;
    }

    setHistoryOpen(true);
    loadLoginHistory(nextUser);
  }, [filteredUsers, loadLoginHistory, roleFilter, selectedUser]);

  const moveHistoryUser = useCallback((direction) => {
    if (!filteredUsers.length) return;
    const currentIndex = selectedUserIndex >= 0 ? selectedUserIndex : 0;
    const nextIndex = (currentIndex + direction + filteredUsers.length) % filteredUsers.length;
    const nextUser = filteredUsers[nextIndex];
    if (!nextUser) return;
    loadUserActions(nextUser, adminUserId);
    loadLoginHistory(nextUser, adminUserId);
  }, [adminUserId, filteredUsers, loadLoginHistory, loadUserActions, selectedUserIndex]);

  const saveUserRole = useCallback(async (user, nextRole) => {
    const selectedUserId = String(user?.userId || "").trim();
    const cleanRole = String(nextRole || "").trim();
    if (hasTaskAddition(user)) {
      setRoleMessage("Director is controlled by Admin Control.");
      return;
    }
    if (!selectedUserId || !adminUserId || !USER_ROLE_OPTIONS.includes(cleanRole)) return;

    setRoleSaving(true);
    setRoleMessage("");

    try {
      const response = await fetch(USAGE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          script: "updateUserRole",
          userId: String(adminUserId),
          selectedUserId,
          role: cleanRole,
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setRoleMessage(data?.error || "Unable to save role.");
        return;
      }

      const savedRole = data.role || cleanRole;
      setUsers((currentUsers) => currentUsers.map((row) => (
        String(row?.userId || "") === selectedUserId ? { ...row, role: savedRole } : row
      )));
      setSelectedUser((currentUser) => (
        String(currentUser?.userId || "") === selectedUserId ? { ...currentUser, role: savedRole } : currentUser
      ));
      setHistoryUser((currentUser) => (
        String(currentUser?.userId || "") === selectedUserId ? { ...currentUser, role: savedRole } : currentUser
      ));
      setRoleMessage("Saved.");
    } catch {
      setRoleMessage("Unable to save role.");
    } finally {
      setRoleSaving(false);
    }
  }, [adminUserId]);

  const loadTeamActions = useCallback(async (teamName, userId = adminUserId) => {
    const cleanTeamName = String(teamName || "").trim();
    if (!cleanTeamName || !userId) return;

    setTeamDisplayMode(true);
    setSelectedTeam(cleanTeamName);
    setSelectedUser(null);
    setSelectedScope("team");
    setActionsLoading(true);
    setActionsError("");
    setActionCounts({});
    setSelectedAction("");
    setActionEvents([]);
    setActionEventsError("");
    setRoleMessage("");

    try {
      const response = await fetch(USAGE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          script: "getTodayTeamUsageCounts",
          userId: String(userId),
          teamName: cleanTeamName,
          startDate,
          endDate,
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setActionsError(data?.error || "Unable to load team data.");
        setActionCounts({});
      } else {
        setActionCounts(data.counts || {});
      }
    } catch {
      setActionsError("Unable to load team data.");
      setActionCounts({});
    } finally {
      setActionsLoading(false);
    }
  }, [adminUserId, endDate, startDate]);

  const loadActionEvents = useCallback(async (eventLabel) => {
    const selectedUserId = String(selectedUser?.userId || "").trim();
    const cleanEventLabel = String(eventLabel || "").trim();
    const cleanTeamName = String(selectedTeam || "").trim();
    if (!cleanEventLabel || !adminUserId) return;
    if (selectedScope === "team" && !cleanTeamName) return;
    if (selectedScope !== "team" && !selectedUserId) return;

    setSelectedAction(cleanEventLabel);
    setActionEventsLoading(true);
    setActionEventsError("");
    setActionEvents([]);

    try {
      const response = await fetch(USAGE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
	        body: new URLSearchParams({
	          script: selectedScope === "team" ? "getTodayTeamUsageEvents" : "getTodayUserUsageEvents",
	          userId: String(adminUserId),
	          ...(selectedScope === "team" ? { teamName: cleanTeamName } : { selectedUserId }),
	          eventLabel: cleanEventLabel,
	          startDate,
	          endDate,
	        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setActionEventsError(data?.error || "Unable to load action times.");
        setActionEvents([]);
      } else {
        setActionEvents(Array.isArray(data.events) ? data.events : []);
      }
    } catch {
      setActionEventsError("Unable to load action times.");
      setActionEvents([]);
    } finally {
      setActionEventsLoading(false);
    }
  }, [adminUserId, endDate, selectedScope, selectedTeam, selectedUser, startDate]);

  const loadButtonCounts = useCallback(async (userId = adminUserId) => {
    const cleanUserId = String(userId || "").trim();
    if (!cleanUserId) return;

    setButtonsLoading(true);
    setButtonsError("");
    setButtonCounts({});

    try {
      const response = await fetch(USAGE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          script: "getTodayAllUsageCounts",
          userId: cleanUserId,
          startDate,
          endDate,
          excludeFbx: adminFullAccess && excludeFbxButtons ? "1" : "0",
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setButtonsError(data?.error || "Unable to load button data.");
        setButtonCounts({});
      } else {
        setButtonCounts(data.counts || {});
      }
    } catch {
      setButtonsError("Unable to load button data.");
      setButtonCounts({});
    } finally {
      setButtonsLoading(false);
    }
  }, [adminFullAccess, adminUserId, endDate, excludeFbxButtons, startDate]);

  const loadButtonUsers = useCallback(async (eventLabel) => {
    const cleanEventLabel = String(eventLabel || "").trim();
    if (!cleanEventLabel || !adminUserId) return;

    setSelectedButtonAction(cleanEventLabel);
    setButtonUsersLoading(true);
    setButtonUsersError("");
    setButtonUsers([]);

    try {
      const response = await fetch(USAGE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          script: "getTodayAllUsageEvents",
          userId: String(adminUserId),
          eventLabel: cleanEventLabel,
          startDate,
          endDate,
          excludeFbx: adminFullAccess && excludeFbxButtons ? "1" : "0",
        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setButtonUsersError(data?.error || "Unable to load button users.");
        setButtonUsers([]);
      } else {
        setButtonUsers(Array.isArray(data.users) ? data.users : []);
      }
    } catch {
      setButtonUsersError("Unable to load button users.");
      setButtonUsers([]);
    } finally {
      setButtonUsersLoading(false);
    }
  }, [adminFullAccess, adminUserId, endDate, excludeFbxButtons, startDate]);

  const selectButtonTab = (tabKey) => {
    setActiveButtonTab(tabKey);
    setSelectedButtonAction("");
    setButtonUsers([]);
    setButtonUsersError("");
  };

  const openButtonsPanel = useCallback(async () => {
    let cleanUserId = String(adminUserId || "").trim();
    if (!cleanUserId) {
      const user = await getUserFromToken().catch(() => null);
      if (!isAdminUser(user)) {
        setButtonsError("Admin access required.");
        setButtonsOpen(true);
        return;
      }
      cleanUserId = String(user?.scout ?? "");
      setAdminUserId(cleanUserId);
      setAdminFullAccess(isFbxTeam(user?.team));
    }

    setButtonsOpen(true);
    setActiveButtonTab("2026");
    setSelectedButtonAction("");
    setButtonUsers([]);
    setButtonUsersError("");
    loadButtonCounts(cleanUserId);
  }, [adminUserId, loadButtonCounts]);

  useEffect(() => {
    if (!buttonsOpen || !adminUserId) return;
    loadButtonCounts(adminUserId);
    if (selectedButtonAction) {
      loadButtonUsers(selectedButtonAction);
    }
  }, [adminFullAccess, adminUserId, buttonsOpen, endDate, excludeFbxButtons, loadButtonCounts, loadButtonUsers, selectedButtonAction, startDate]);

  const loadTeamUsers = useCallback(async (teamName, userId = adminUserId, options = {}) => {
    const cleanTeamName = String(teamName || "").trim();
    if (!cleanTeamName || !userId) return;

    const keepTeamDisplayMode = options?.keepTeamDisplayMode === true;
    const allowActionDetails = options?.allowActionDetails ?? adminFullAccess;

    setSelectedTeam(cleanTeamName);
    setUsersLoading(true);
    setUsersError("");
    setUsers([]);
    setSelectedUser(null);
    setTeamDisplayMode(keepTeamDisplayMode);
    setSelectedScope(keepTeamDisplayMode ? "team" : "user");
    setActionCounts({});
    setSelectedAction("");
    setActionEvents([]);
    setActionEventsError("");

    try {
      const response = await fetch(USAGE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
	        body: new URLSearchParams({
	          script: "getTodayTeamUserSignIns",
	          userId: String(userId),
	          teamName: cleanTeamName,
	          startDate,
	          endDate,
	        }).toString(),
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        setUsersError(data?.error || "Unable to load team users.");
        setUsers([]);
      } else {
        const nextUsers = Array.isArray(data.users) ? data.users : [];
        setUsers(nextUsers);
        if (keepTeamDisplayMode && allowActionDetails) {
          loadTeamActions(cleanTeamName, userId);
        } else if (nextUsers[0]) {
          loadUserActions(nextUsers[0], userId);
        }
      }
    } catch {
      setUsersError("Unable to load team users.");
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [adminFullAccess, adminUserId, endDate, loadTeamActions, loadUserActions, startDate]);

  useEffect(() => {
    let isCurrent = true;

    const loadTodayStats = async () => {
      setLoading(true);
      setError("");

      const user = await getUserFromToken().catch(() => null);
      if (!isAdminUser(user)) {
        if (isCurrent) {
          setError("Admin access required.");
          setLoading(false);
        }
        return;
      }
      const cleanUserId = String(user?.scout ?? "");
      setAdminUserId(cleanUserId);
      setAdminFullAccess(isFbxTeam(user?.team));

      try {
        const response = await fetch(USAGE_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
	        body: new URLSearchParams({
	            script: "getTodayTeamSignIns",
	            userId: cleanUserId,
	            startDate,
	            endDate,
	          }).toString(),
        });
        const data = await response.json();

        if (!isCurrent) return;

	        if (!response.ok || !data?.success) {
	          setError(data?.error || "Unable to load usage data.");
	          setTeams([]);
		        } else {
		          const nextTeams = Array.isArray(data.teams) ? data.teams : [];
              const access = data.access || {};
              const nextAdminFullAccess = Boolean(access.isFullAccess);
              setAdminFullAccess(nextAdminFullAccess);
			          setDateLabel(data.date || "");
		          setTeams(nextTeams);
		          if (nextTeams.length > 0) {
		            loadTeamUsers(nextTeams[0].teamName, cleanUserId, { allowActionDetails: nextAdminFullAccess });
	          } else {
	            setSelectedTeam("");
	            setUsers([]);
	            setSelectedUser(null);
              setSelectedScope("user");
	            setActionCounts({});
	            setSelectedAction("");
	            setActionEvents([]);
	          }
	        }
      } catch {
        if (isCurrent) {
          setError("Unable to load usage data.");
          setTeams([]);
        }
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    loadTodayStats();

    return () => {
      isCurrent = false;
    };
  }, [endDate, loadTeamUsers, startDate]);

  const visibleTrackedActions = TRACKED_ACTIONS.filter((label) => !showOnlyClicked || Number(actionCounts?.[label] || 0) > 0);
  const visible2026Actions = visibleTrackedActions.filter((label) => !isLiteButtonAction(label));
  const visibleLiteActions = visibleTrackedActions.filter(isLiteButtonAction);
  const clickedTrackedActions = TRACKED_ACTIONS.filter((label) => Number(actionCounts?.[label] || 0) > 0);
  const clicked2026Actions = clickedTrackedActions.filter((label) => !isLiteButtonAction(label));
  const clickedLiteActions = clickedTrackedActions.filter(isLiteButtonAction);
  const activeButtonGroups = BUTTON_ACTION_TABS[activeButtonTab] || BUTTON_ACTION_TABS["2026"];
  const selectedDateLabel = getDateRangeLabel(startDate, endDate, dateLabel || "Today");
  const renderActionButton = (label) => {
    const count = Number(actionCounts?.[label] || 0);
    return (
      <button
        type="button"
        className="admin-data-action-button"
        disabled={count === 0}
        key={label}
        onClick={() => loadActionEvents(label)}
      >
        <span title={label}>{label}</span>
        <strong>{count}</strong>
      </button>
    );
  };
  const renderReadonlyAction = (label) => {
    const count = Number(actionCounts?.[label] || 0);
    return (
    <div className="admin-data-action-button admin-data-action-button-readonly" key={label}>
      <span title={label}>{label}</span>
      <strong>{count}</strong>
    </div>
    );
  };

  return (
    <div className="admin-data-modal" role="dialog" aria-modal="true" aria-label="Data">
      <div className="admin-data-modal-header">
	        <div>
	          <strong>Data</strong>
		          <span>{selectedDateLabel} | {adminFullAccess ? "All Teams" : "People"} | {version}</span>
	          <div className="admin-data-date-filter">
	            <span>From</span>
	            <FBXCalendar
	              fieldName="adminStartDate"
	              placeholder="Start Date"
	              dateIn={startDate}
	              minYear="2024"
	              maxYear={todayYMD.slice(0, 4)}
	              isOpen={openCalendar === "adminStartDate"}
	              onDateChange={handleStartDateChange}
	              onToggleCalendar={handleToggleCalendar}
	              style={{ width: 145 }}
	            />
		                    </div>}
	          <div className="admin-data-date-filter">
	            <span>To</span>
	            <FBXCalendar
	              fieldName="adminEndDate"
	              placeholder="End Date"
	              dateIn={endDate}
	              minYear="2024"
	              maxYear={todayYMD.slice(0, 4)}
	              isOpen={openCalendar === "adminEndDate"}
	              onDateChange={handleEndDateChange}
	              onToggleCalendar={handleToggleCalendar}
	              style={{ width: 145 }}
	            />
	          </div>
        </div>
        <div className="admin-data-header-actions">
          {adminFullAccess && (
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={openButtonsPanel}>
              Buttons
            </button>
          )}
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={openHistoryPanel}>
            History
          </button>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={closePanel}>
            Close
          </button>
        </div>
      </div>

      <div className="admin-data-panel">
        {loading && <div className="admin-data-muted">Loading...</div>}

        {!loading && error && (
          <div className="alert alert-danger py-2 mb-0">{error}</div>
        )}

        {!loading && !error && (
          <>
	            <div className={`admin-data-grid ${adminFullAccess ? "" : "admin-data-grid-team-scoped"}`}>
	              {adminFullAccess && (
	                <section className="admin-data-section">
	                  <div className="admin-data-header">
	                    <div>Team</div>
	                  <div className="text-end">{adminFullAccess ? "Sign Ins / Activity" : "Sign Ins"}</div>
	                  </div>

	                  {teams.length === 0 ? (
		                    <div className="admin-data-empty">No team activity for this date range.</div>
	                  ) : (
	                    <div className="admin-data-list">
	                      {teams.map((team) => (
	                        <button
	                          type="button"
	                          className={`admin-data-row admin-data-team-row ${cleanTeamCode(selectedTeam) === cleanTeamCode(team.teamName) ? "admin-data-row-selected" : ""}`}
	                          key={team.teamName}
	                          onClick={() => loadTeamUsers(team.teamName, adminUserId, { keepTeamDisplayMode: teamDisplayMode })}
	                        >
	                          <span className="admin-data-team" title={team.teamName}>{team.teamName}</span>
	                          <span className="admin-data-count">{getUsageListCount(team)}</span>
	                        </button>
	                      ))}
	                    </div>
	                  )}
	                </section>
	              )}

              <section className="admin-data-section">
                <div className="admin-data-header">
                  <div className="admin-data-users-title">
	                    <span>{adminFullAccess ? (selectedTeam || "Team Users") : "People"}</span>
                    <label className="admin-data-role-filter">
                      <span>Role</span>
                      <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
                        {ROLE_FILTER_OPTIONS.map((role) => (
                          <option value={role} key={role}>{role}</option>
                        ))}
                      </select>
                    </label>
	                    {adminFullAccess && selectedTeam && (
                      <button
                        type="button"
                        className={`admin-data-team-header-button ${teamDisplayMode ? "admin-data-team-header-button-selected" : ""}`}
                        onClick={() => loadTeamActions(selectedTeam)}
                      >
                        Team
                      </button>
                    )}
                  </div>
                  <div className="text-end">Sign Ins / Activity</div>
                </div>

                {usersLoading && <div className="admin-data-muted">Loading...</div>}

                {!usersLoading && usersError && (
                  <div className="alert alert-danger py-2 m-2">{usersError}</div>
                )}

                {!usersLoading && !usersError && selectedTeam && users.length === 0 && (
	                  <div className="admin-data-empty">No user activity for this team and date range.</div>
                )}

                {!usersLoading && !usersError && selectedTeam && users.length > 0 && filteredUsers.length === 0 && (
	                  <div className="admin-data-empty">No people match this role filter.</div>
                )}

                {!usersLoading && !usersError && !selectedTeam && (
                  <div className="admin-data-empty">Select a team.</div>
                )}

                {!usersLoading && !usersError && filteredUsers.length > 0 && (
                  <div className="admin-data-list">
                    {filteredUsers.map((user) => (
                      <button
                        type="button"
                        className={`admin-data-row admin-data-team-row ${selectedUser?.userId === user.userId ? "admin-data-row-selected" : ""} ${hasTaskAddition(user) ? "admin-data-task-user-row" : ""}`}
                        key={`${user.userId}-${user.userName}`}
                        onClick={() => loadUserActions(user)}
                      >
                        <span className="admin-data-user-cell" title={`${user.userName} ${user.lastSignIn || ""}`.trim()}>
	                          <span className="admin-data-user-main">
                              <span className="admin-data-team">{user.userName}</span>
                              <span className="admin-data-user-subline">
                                Last {user.lastSignIn || "-"}
                              </span>
                            </span>
                            <span className="admin-data-user-badges">
                              {hasTaskAddition(user) && <span className="admin-data-task-badge">Tasks</span>}
	                            <span className="admin-data-version-badge">{user.appVersion || "-"}</span>
                            </span>
                        </span>
	                        <span className="admin-data-count">{getUsageListCount(user, adminFullAccess)}</span>
                      </button>
                    ))}
	                    {adminFullAccess && (
	                      <button
	                        type="button"
	                        className={`admin-data-row admin-data-team-row admin-data-team-total-row ${selectedScope === "team" ? "admin-data-row-selected admin-data-team-mode-row" : ""}`}
	                        onClick={() => loadTeamActions(selectedTeam)}
	                      >
	                        <span className="admin-data-team">Team</span>
	                        <span className="admin-data-count">{selectedTeamRecord ? getUsageListCount(selectedTeamRecord) : ""}</span>
	                      </button>
	                    )}
                  </div>
                )}
              </section>

              <section className="admin-data-section admin-data-detail-section">
                <div className="admin-data-header">
                  <div>Data</div>
                  <div />
                </div>

                {selectedScope !== "team" && !selectedUser ? (
	                  <div className="admin-data-empty">Select a person.</div>
                ) : (
                  <div className="admin-data-detail">
                    <div className="admin-data-detail-line">
                      <span className="admin-data-detail-name" title={selectedScope === "team" ? selectedTeam : selectedUser.userName}>
                        {selectedScope === "team" ? `${selectedTeam} Team` : selectedUser.userName}
                      </span>
		                      {adminFullAccess && <span><span>Team</span> <strong>{selectedTeam || "-"}</strong></span>}
		                      <span><span>Sign Ins</span> <strong>{selectedScope === "team" ? (selectedTeamRecord?.signInCount ?? "-") : selectedUser.signInCount}</strong></span>
	                          {adminFullAccess && <span><span>Activity</span> <strong>{selectedScope === "team" ? (selectedTeamRecord?.usageCount ?? "-") : (selectedUser.usageCount ?? "-")}</strong></span>}
                          {selectedScope !== "team" && (
                            <>
                              <span><span>Last</span> <strong>{selectedUser.lastSignIn || "-"}</strong></span>
                            </>
                          )}
                      {selectedScope === "team" ? (
                        <span><span>Users</span> <strong>{users.length}</strong></span>
	                      ) : (
	                        <>
				                        <span><span>Sign-in Version</span> <strong>{selectedUser.appVersion || "-"}</strong></span>
			                        {adminFullAccess && <span><span>User ID</span> <strong>{selectedUser.userId || "-"}</strong></span>}
		                        <span className="admin-data-role-control">
		                          <span>Role</span>
		                          {hasTaskAddition(selectedUser) ? (
		                            <strong>Director</strong>
		                          ) : (
		                            <select
		                              value={getDisplayRole(selectedUser)}
		                              onChange={(event) => saveUserRole(selectedUser, event.target.value)}
		                              disabled={roleSaving}
		                            >
		                              {USER_ROLE_OPTIONS.map((role) => (
		                                <option value={role} key={role}>{role}</option>
		                              ))}
		                            </select>
		                          )}
		                        </span>
	                        </>
	                      )}
	                    </div>
	                    {roleMessage && <div className="admin-data-role-message">{roleMessage}</div>}
	
	                    <div className="admin-data-action-area">
	                      {actionsLoading && <div className="admin-data-muted">Loading...</div>}

	                      {!actionsLoading && actionsError && (
	                        <div className="alert alert-danger py-2 mb-0">{actionsError}</div>
	                      )}

	                      {!actionsLoading && !actionsError && adminFullAccess && (
	                        <>
	                          <label className="admin-data-clicked-toggle">
	                            <input
	                              type="checkbox"
	                              checked={showOnlyClicked}
	                              onChange={(event) => setShowOnlyClicked(event.target.checked)}
	                            />
	                            <span>Show only clicked</span>
	                          </label>

	                          <div className="admin-data-action-grid">
	                            {visible2026Actions.map(renderActionButton)}
	                            {visibleLiteActions.length > 0 && (
	                              <div className="admin-data-action-lite-separator">
	                                <span>Lite Data</span>
	                              </div>
	                            )}
	                            {visibleLiteActions.map(renderActionButton)}
	                          </div>
	                        </>
	                      )}

	                      {!actionsLoading && !actionsError && !adminFullAccess && (
	                        clickedTrackedActions.length > 0 ? (
	                          <div className="admin-data-action-grid">
	                            {clicked2026Actions.map(renderReadonlyAction)}
	                            {clickedLiteActions.length > 0 && (
	                              <div className="admin-data-action-lite-separator">
	                                <span>Lite Data</span>
	                              </div>
	                            )}
	                            {clickedLiteActions.map(renderReadonlyAction)}
	                          </div>
	                        ) : (
	                          <div className="admin-data-empty admin-data-events-empty">No activity found.</div>
	                        )
	                      )}
	                    </div>

	                    {adminFullAccess && (
	                      <div className="admin-data-events-area">
	                          {!selectedAction && (
	                            <div className="admin-data-empty admin-data-events-empty">Select an enabled action.</div>
	                          )}

	                          {selectedAction && (
	                            <>
	                              <div className="admin-data-events-title">{selectedAction}</div>

	                              {actionEventsLoading && <div className="admin-data-muted">Loading...</div>}

	                              {!actionEventsLoading && actionEventsError && (
	                                <div className="alert alert-danger py-2 mb-0">{actionEventsError}</div>
	                              )}

	                              {!actionEventsLoading && !actionEventsError && actionEvents.length === 0 && (
	                                <div className="admin-data-empty admin-data-events-empty">No times found.</div>
	                              )}

	                              {!actionEventsLoading && !actionEventsError && actionEvents.length > 0 && (
	                                <div className="admin-data-events-list">
	                                  {actionEvents.map((event, index) => {
	                                    const summaryParts = getUsageSummaryParts(event);
	                                    const summary = summaryParts.join(" | ");
	                                    const actorName = selectedScope === "team"
	                                      ? (event.userName || event.userId || "Unknown")
	                                      : "";
	                                    return (
	                                      <div className="admin-data-event-row" key={`${event.eventDateTime || event.eventTime}-${index}`}>
	                                        <div className="admin-data-event-main">
	                                          <span title={[event.eventLabel, summary].filter(Boolean).join(" - ")}>
	                                            {actorName && (
	                                              <>
	                                                <strong className="admin-data-event-actor">{actorName}</strong>
	                                                {" - "}
	                                              </>
	                                            )}
	                                            {event.eventLabel}
	                                            {summaryParts.length > 0 && (
	                                              <>
	                                                {" - "}
	                                                <span className="admin-data-event-summary-inline">{summary}</span>
	                                              </>
	                                            )}
	                                          </span>
	                                          <strong>{event.eventDateTime || event.eventTime}</strong>
	                                        </div>
	                                      </div>
	                                    );
	                                  })}
	                                </div>
	                              )}
	                            </>
	                          )}
	                        </div>
	                    )}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>

      {historyOpen && (
        <div className="admin-data-buttons-overlay">
          <div className="admin-data-history-panel" role="dialog" aria-modal="true" aria-label="Login history">
            <div className="admin-data-buttons-header">
              <div>
                <strong>History</strong>
                <span>Last 20 Logins | {historyUser?.userName || historyUser?.userId || "Person"}</span>
              </div>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setHistoryOpen(false)}>
                Close
              </button>
            </div>

            <div className="admin-data-history-nav">
              <button
                type="button"
                className="admin-data-history-arrow"
                onClick={() => moveHistoryUser(-1)}
                disabled={filteredUsers.length <= 1}
                aria-label="Previous person"
              >
                &lt;
              </button>
              <span>{selectedUserIndex >= 0 ? `${selectedUserIndex + 1} of ${filteredUsers.length}` : ""}</span>
              <button
                type="button"
                className="admin-data-history-arrow"
                onClick={() => moveHistoryUser(1)}
                disabled={filteredUsers.length <= 1}
                aria-label="Next person"
              >
                &gt;
              </button>
            </div>

            <div className="admin-data-history-body">
              {historyLoading && <div className="admin-data-muted">Loading...</div>}

              {!historyLoading && historyError && (
                <div className="alert alert-danger py-2 m-2">{historyError}</div>
              )}

              {!historyLoading && !historyError && historyLogins.length === 0 && (
                <div className="admin-data-empty">No login history found.</div>
              )}

              {!historyLoading && !historyError && historyLogins.length > 0 && (
                <div className="admin-data-history-table">
                  <div className="admin-data-history-row admin-data-history-heading">
                    <span>Date</span>
                    <span>Time</span>
                  </div>
                  {historyLogins.map((login, index) => (
                    <div className="admin-data-history-row" key={`${login.date}-${login.time}-${index}`}>
                      <span>{formatLoginDate(login.date)}</span>
                      <span>{login.time || "-"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

	      {buttonsOpen && (
        <div className="admin-data-buttons-overlay">
          <div className="admin-data-buttons-panel" role="dialog" aria-modal="true" aria-label="Button usage">
            <div className="admin-data-buttons-header">
              <div>
                <strong>Buttons</strong>
                <span>{selectedDateLabel}</span>
              </div>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setButtonsOpen(false)}>
                Close
              </button>
            </div>

            <div className="admin-data-button-tabs" role="tablist" aria-label="Button type">
              {Object.keys(BUTTON_ACTION_TABS).map((tabKey) => (
                <button
                  type="button"
                  className={`admin-data-button-tab ${activeButtonTab === tabKey ? "admin-data-button-tab-active" : ""}`}
                  key={tabKey}
                  onClick={() => selectButtonTab(tabKey)}
                  role="tab"
                  aria-selected={activeButtonTab === tabKey}
                >
                  {BUTTON_ACTION_TAB_LABELS[tabKey]}
                </button>
              ))}
              {adminFullAccess && (
                <button
                  type="button"
                  className={`admin-data-exclude-fbx-button ${excludeFbxButtons ? "admin-data-exclude-fbx-button-active" : ""}`}
                  onClick={() => setExcludeFbxButtons((current) => !current)}
                >
                  Exclude FBX
                </button>
              )}
            </div>

            <div className="admin-data-buttons-body">
              <section className="admin-data-section">
                <div className="admin-data-header">
                  <div>Button</div>
                  <div className="text-end">Uses</div>
                </div>

                {buttonsLoading && <div className="admin-data-muted">Loading...</div>}

                {!buttonsLoading && buttonsError && (
                  <div className="alert alert-danger py-2 m-2">{buttonsError}</div>
                )}

                {!buttonsLoading && !buttonsError && (
                  <div className="admin-data-list">
                    {activeButtonGroups.map((group, groupIndex) => (
                      <React.Fragment key={`button-group-${groupIndex}`}>
                        {groupIndex > 0 && <div className="admin-data-button-separator" />}
                        {group.map((label) => {
                          const count = Number(buttonCounts?.[label] || 0);
                          return (
                            <button
                              type="button"
                              className={`admin-data-row admin-data-team-row ${selectedButtonAction === label ? "admin-data-row-selected" : ""}`}
                              disabled={count === 0}
                              key={label}
                              onClick={() => loadButtonUsers(label)}
                            >
                              <span className="admin-data-team" title={label}>{getButtonLabel(label)}</span>
                              <span className="admin-data-count">{count}</span>
                            </button>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </section>

              <section className="admin-data-section">
                <div className="admin-data-header admin-data-button-users-header">
                  <div>{selectedButtonAction ? getButtonLabel(selectedButtonAction) : "People"}</div>
                  <div className="text-end">Uses</div>
                </div>

                {!selectedButtonAction && (
                  <div className="admin-data-empty">Select a button.</div>
                )}

                {selectedButtonAction && buttonUsersLoading && <div className="admin-data-muted">Loading...</div>}

                {selectedButtonAction && !buttonUsersLoading && buttonUsersError && (
                  <div className="alert alert-danger py-2 m-2">{buttonUsersError}</div>
                )}

                {selectedButtonAction && !buttonUsersLoading && !buttonUsersError && buttonUsers.length === 0 && (
                  <div className="admin-data-empty">No users found for this button.</div>
                )}

                {selectedButtonAction && !buttonUsersLoading && !buttonUsersError && buttonUsers.length > 0 && (
                  <div className="admin-data-list">
                    {buttonUsers.map((user, index) => (
	                      <div className="admin-data-row admin-data-button-user-row" key={`${user.userId || user.userName}-${user.teamName}-${index}`}>
	                        <span className="admin-data-user-cell" title={`${user.userName || "Unknown"} ${adminFullAccess ? user.teamName || "" : ""}`.trim()}>
                          <span className="admin-data-user-main">
                            <span className="admin-data-team">{user.userName || user.userId || "Unknown"}</span>
                            <span className="admin-data-user-subline">
	                              {adminFullAccess ? `${user.teamName || "Unknown"} | ` : ""}Last {user.lastEventDateTime || "-"}
                            </span>
                          </span>
                          <span className="admin-data-user-badges">
                            <span className="admin-data-version-badge">{user.appVersion || "-"}</span>
                          </span>
                        </span>
                        <span className="admin-data-count">{user.eventCount}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminData;
